
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useReportMatching } from "./useReportMatching";
import { useRadiusMatching } from "./useRadiusMatching";
import { useTerminalMarketPrices } from "./useTerminalMarketPrices";

interface UsdaReport {
  id: string;
  report_id: string;
  slug_id: string | null;
  slug_name: string | null;
  report_name: string | null;
  published_date: string | null;
  market_types: any;
  commodities: string[] | null;
  varieties: string[] | null;
  market_cities: string[] | null;
  report_type: string | null;
  created_at: string;
  updated_at: string;
}

interface ProfileReport {
  id: string;
  profile_id: string;
  report_id: string;
  market_city: string;
  relevance_score: number;
  report_name: string | null;
  market_types: any;
  report_type: string | null;
  created_at: string;
  updated_at: string;
  usda_reports: UsdaReport;
}

export const useUsdaReports = () => {
  const [userReports, setUserReports] = useState<ProfileReport[]>([]);
  const [allReports, setAllReports] = useState<UsdaReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { 
    triggerReportMatching, 
    fixNullMarketData, 
    loading: matchingLoading, 
    error: matchingError 
  } = useReportMatching();

  const {
    triggerRadiusMatching,
    loading: radiusLoading
  } = useRadiusMatching();

  const { 
    terminalMarketPrices, 
    fetchTerminalMarketPricesFromUserCrops, 
    fetchAndStoreProfilePrices 
  } = useTerminalMarketPrices();

  const fetchUserReports = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setUserReports([]);
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("id, location")
        .eq("user_id", user.id)
        .single();

      if (!profile) {
        console.log('No profile found for user');
        setUserReports([]);
        return;
      }

      console.log('User profile:', profile);

      // Fetch profile reports first
      const { data: profileReportsData, error: profileReportsError } = await supabase
        .from("profile_reports")
        .select("*")
        .eq("profile_id", profile.id)
        .order("relevance_score", { ascending: false });

      if (profileReportsError) {
        throw profileReportsError;
      }

      // Then fetch the related USDA reports separately
      if (profileReportsData && profileReportsData.length > 0) {
        const reportIds = profileReportsData.map(pr => pr.report_id);
        const { data: usdaReportsData, error: usdaReportsError } = await supabase
          .from("usda_reports")
          .select("*")
          .in("id", reportIds);

        if (usdaReportsError) {
          throw usdaReportsError;
        }

        // Create a map of USDA reports by ID
        const usdaReportsMap = new Map();
        usdaReportsData?.forEach(report => {
          usdaReportsMap.set(report.id, report);
        });

        // Combine the data manually
        const combinedReports: ProfileReport[] = profileReportsData.map(profileReport => ({
          ...profileReport,
          usda_reports: usdaReportsMap.get(profileReport.report_id) || {
            id: '',
            report_id: '',
            slug_id: null,
            slug_name: null,
            report_name: null,
            published_date: null,
            market_types: null,
            commodities: null,
            varieties: null,
            market_cities: null,
            report_type: null,
            created_at: '',
            updated_at: ''
          }
        }));

        setUserReports(combinedReports);
      } else {
        setUserReports([]);
      }

      console.log('Fetched user reports:', profileReportsData?.length || 0);
    } catch (err) {
      console.error("Error fetching user reports:", err);
      setError('Failed to load user reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllReports = async () => {
    try {
      const { data: reports, error } = await supabase
        .from("usda_reports")
        .select("*")
        .order("published_date", { ascending: false })
        .limit(50);

      if (error) throw error;

      console.log('All reports fetched:', reports?.length || 0);
      setAllReports(reports || []);
    } catch (err) {
      console.error("Error fetching all reports:", err);
      setError('Failed to load reports');
    }
  };

  const fetchReportsByType = async (reportType: string) => {
    try {
      const { data: reports, error } = await supabase
        .from("usda_reports")
        .select("*")
        .eq("report_type", reportType)
        .order("published_date", { ascending: false })
        .limit(20);

      if (error) throw error;

      return reports || [];
    } catch (err) {
      console.error("Error fetching reports by type:", err);
      return [];
    }
  };

  const storeUsdaReport = async (reportData: any) => {
    try {
      console.log('Storing USDA report:', reportData.report_id);
      
      const { data, error } = await supabase
        .from("usda_reports")
        .upsert({
          report_id: reportData.report_id,
          slug_id: reportData.slug_id,
          slug_name: reportData.slug_name,
          report_name: reportData.report_name,
          published_date: reportData.published_date,
          market_types: reportData.market_types,
          commodities: reportData.commodities,
          varieties: reportData.varieties,
          market_cities: reportData.market_cities,
          report_type: reportData.report_type || 'other'
        }, {
          onConflict: 'report_id'
        })
        .select()
        .single();

      if (error) throw error;

      console.log('Successfully stored USDA report:', data?.id);
      return data;
    } catch (err) {
      console.error("Error storing USDA report:", err);
      throw err;
    }
  };

  const triggerEnhancedMatching = async () => {
    try {
      console.log('🎯 Starting enhanced radius-based matching...');
      await triggerRadiusMatching();
      await fetchUserReports();
      console.log('✅ Enhanced matching completed');
    } catch (error) {
      console.error('Error in enhanced matching:', error);
      throw error;
    }
  };

  useEffect(() => {
    fetchUserReports();
    fetchAllReports();
  }, []);

  return {
    userReports,
    allReports,
    terminalMarketPrices,
    loading: loading || matchingLoading || radiusLoading,
    error: error || matchingError,
    storeUsdaReport,
    triggerReportMatching,
    triggerEnhancedMatching,
    fixNullMarketData,
    refreshUserReports: fetchUserReports,
    refreshAllReports: fetchAllReports,
    refreshTerminalMarketPrices: fetchTerminalMarketPricesFromUserCrops,
    fetchAndStoreProfilePrices,
    fetchReportsByType
  };
};
